import { type SiteId } from '@solinkcorp/api-types';
import { Button } from '@solinkcorp/solink-ui/components/Button';
import {
  LocationsFilterButton,
  LocationsFilterIconButton,
} from '@solinkcorp/solink-ui/components/LocationsFilter';
import { Popover, PopoverContent, PopoverTrigger } from '@solinkcorp/solink-ui/components/Popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@solinkcorp/solink-ui/components/Tooltip';
import { buttonVariants } from '@solinkcorp/solink-ui/components/ui/button';
import { Calendar } from '@solinkcorp/solink-ui/components/ui/calendar';
import { useSidebar } from '@solinkcorp/solink-ui/components/ui/sidebar';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { isToday as checkIsToday } from '@solinkcorp/utils';
import {
  type DayOfWeekNumber,
  HISTOGRAM_RANGE,
  HistogramApiHelper,
  type HistogramRange,
} from '@solinkcorp/vd-common';
import { decrementDate, incrementDate, setFilterDate, setRange } from 'actions/filterActions';
import { CommonContext } from 'components/AuthenticatedApp/AuthenticatedApp';
import { areCapsulesFilteredByDateTimeField } from 'components/EventFilter/utils';
import { shouldCommitDraftRange } from 'components/NavigationFrame/navigation/dateFilterPolicy';
import { usePlayingCamerasContext } from 'components/PlayingCamerasContext';
import { CalendarIcon, Check, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import AnalyticsLogger from 'models/AnalyticsLogger';
import moment from 'moment';
import { type FC, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useControllerState } from 'solox';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import CameraStore from 'stores/CameraStore';
import EventFilterStore from 'stores/EventFilterStore';
import { useDispatch, useSelector } from 'stores/store';
import { useEventStandard } from 'utils/EventStandardCache/eventStandardCache';
import FeaturesUtil from 'utils/FeaturesUtil';
import { type CameraGroupWithCameras } from 'utils/types';
import {
  calendarDay,
  clampInstant,
  datePickerLocale,
  draftNeedsCommitOnClose,
  formatClockTime,
  formatPickerLabel,
  formatTimeInputValue,
  formatTimezone,
  instantOnCalendarDay,
  instantWithTimeInput,
  labelWithoutWeekday,
  leftCalendarMonth,
  resolveTimezone,
  selectableInstantBounds,
  timeInputError,
} from './datePickerTime';
import { DatePickerTimeInput } from './DatePickerTimeInput';
import { PeriodJump, type PickerView } from './PeriodJump';

/** Seconds tick here so the rest of the picker does not render on each second. */
function TickingClock({ timeZone }: { timeZone: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return formatClockTime(now, timeZone);
}

// Same header, day, and range treatment as the DatePicker primitive. The keys replace
// the generated Calendar classes, so each string stands on its own.
const calendarNavButtonClassName = cn(
  'rdp-button_previous',
  buttonVariants({ variant: 'ghost' }),
  'size-8 select-none rounded-lg border border-border-muted p-1 aria-disabled:opacity-50',
);

const sidebarCalendarClassNames = {
  months: 'rdp-months relative flex flex-col gap-4 md:flex-row md:gap-10',
  month: 'rdp-month flex w-[18.75rem] flex-col gap-3',
  nav: 'rdp-nav absolute inset-x-0 top-0 flex h-[var(--cell-size)] w-full items-center justify-between gap-1',
  weekdays: 'flex gap-2',
  weekday: 'w-9 flex-none label-sm text-text-muted',
  week: 'mt-2 flex gap-2',
  day: 'group/day w-9 select-none p-0',
  day_button: cn(
    'text-text-primary label-sm',
    'transition-none',
    'hover:bg-interaction-secondary-background-default hover:text-text-primary',
    'dark:hover:bg-interaction-secondary-background-default dark:hover:text-text-primary',
    'disabled:text-interaction-secondary-content-inactive',
    'data-[selected-single=true]:bg-background-accent',
    'data-[selected-single=true]:hover:bg-background-accent',
    'dark:data-[selected-single=true]:bg-background-accent',
    'dark:data-[selected-single=true]:hover:bg-background-accent',
    'data-[selected-single=true]:text-text-primary',
    'dark:data-[selected-single=true]:hover:text-text-primary',
    'data-[range-start=true]:bg-background-accent',
    'data-[range-start=true]:hover:bg-background-accent',
    'dark:data-[range-start=true]:hover:bg-background-accent',
    'data-[range-middle=true]:bg-background-accent',
    'data-[range-middle=true]:hover:bg-background-accent',
    'dark:data-[range-middle=true]:hover:bg-background-accent',
    'data-[range-end=true]:bg-background-accent',
    'data-[range-end=true]:hover:bg-background-accent',
    'dark:data-[range-end=true]:hover:bg-background-accent',
    'data-[range-start=true]:text-text-primary',
    'data-[range-middle=true]:text-text-primary',
    'data-[range-end=true]:text-text-primary',
  ),
  range_start: '',
  range_middle: '',
  range_end: '',
  outside: '[&_button]:text-text-muted',
  today: '[&_button]:text-text-accent',
  caption_label: 'select-none',
  button_previous: calendarNavButtonClassName,
  button_next: cn(
    'rdp-button_next',
    buttonVariants({ variant: 'ghost' }),
    'size-8 select-none rounded-lg border border-border-muted p-1 aria-disabled:opacity-50',
  ),
};

const captionToggleClassName = cn(
  'relative z-10 h-8 rounded-lg px-2 text-text-primary label-md',
  'hover:bg-interaction-secondary-background-default',
);

export interface GlobalDatePickerRangeOption {
  label: number | string;
  value: HistogramRange;
}

interface GlobalDatePickerProps {
  /** Sidebar layout: full-width button when expanded, calendar icon on the collapsed rail. */
  variant: 'expanded' | 'collapsed';
  /** Called to toggle off-live. */
  handleToggleClick?: () => void;
  /** False if the live toggle is currently on. */
  isOnInToggle?: boolean;
  minDate: Date | null | undefined;
  maxDate?: Date | null | undefined;
  /** Called when the selected day or range unit changes. */
  onChangeRangeDate?: () => void;
  /** Called whenever the user applies a selection, even if nothing changed. */
  onDatePicked?: () => void;
  selectedLocationIds: readonly SiteId[];
  showLiveToggle?: boolean;
  /** Turns playback to live. Omitted when live is not available on this page. */
  onGoLive?: () => void;
  showTimeInput?: boolean;
  disabled?: boolean;
  /** Blocks date, time, and range changes. The popover still opens so Go Live stays reachable. */
  lockDateSelection?: boolean;
  /**
   * Range units the page supports. Without them the picker selects a single day and
   * ignores `filter.range`, because the page does not read it.
   */
  rangeOptions?: readonly GlobalDatePickerRangeOption[];
  /** True when the range unit means "the last N ending now" rather than a calendar window. */
  rangeRolling?: boolean;
  /** True when prev and next step by the selected range. False steps one day. */
  navigateByRange?: boolean;
}

const GlobalDatePicker: FC<GlobalDatePickerProps> = (props) => {
  const {
    variant,
    handleToggleClick,
    isOnInToggle,
    selectedLocationIds,
    showLiveToggle,
    onGoLive,
    showTimeInput,
    rangeOptions,
    rangeRolling = false,
    lockDateSelection = false,
    navigateByRange = false,
  } = props;

  const dispatch = useDispatch();
  const locale = useLocale();
  const { firstDayOfWeek } = useContext(CommonContext).authData;

  const {
    date,
    range,
    cameraGroupId,
    timezoneId: timezoneIdOverride,
  } = useSelector((state) => state.filter);

  const playingCamerasController = usePlayingCamerasContext();
  const playingCameraIds = useControllerState(
    playingCamerasController.state,
    (state) => state.playingCameraIds,
  );

  const eventStandard = useEventStandard();
  const fieldStats = useSelector((state) => state.fieldStats.fieldStats);
  const capsules = useFluxStore(EventFilterStore, (store) => store.getAppState().capsules);
  const cameraGroups: CameraGroupWithCameras[] = useFluxStore(CameraStore, (store) =>
    store.getCameraGroups(),
  );

  const disabledByFilters =
    eventStandard && fieldStats
      ? areCapsulesFilteredByDateTimeField(eventStandard, fieldStats, capsules)
      : capsules.length;
  const disabled = props.disabled || !!disabledByFilters;

  const selectedGroup = cameraGroups?.find((group) => group.id === cameraGroupId);
  const timezone = resolveTimezone({
    playingLocationId: playingCameraIds?.[0]?.locationId,
    groupLocationId: selectedGroup?.cameras[0]?.siteId,
    selectedLocationIds,
    timezoneIdOverride,
  });

  const minDate = props.minDate ?? undefined;
  const maxDate = props.maxDate ?? undefined;
  const hasRangeOptions = !!rangeOptions && rangeOptions.length > 0;
  const rangeIsSupported = !!rangeOptions?.some((option) => option.value === range);
  const unit: HistogramRange = rangeIsSupported ? range : HISTOGRAM_RANGE.DAY;

  useEffect(() => {
    if (hasRangeOptions && !rangeIsSupported) {
      dispatch(setRange(HISTOGRAM_RANGE.DAY));
    }
  }, [dispatch, hasRangeOptions, rangeIsSupported]);

  const [open, setOpen] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;
  const [draftUnit, setDraftUnit] = useState<HistogramRange>(unit);
  const [draftDate, setDraftDate] = useState<number>(date);
  const [timeInputValue, setTimeInputValue] = useState(() => formatTimeInputValue(date, timezone));
  const [draftLive, setDraftLive] = useState(false);
  // Instant shown before Live was checked. Unchecking restores it.
  const dateBeforeLive = useRef<number | null>(null);
  const timeInputValueRef = useRef(timeInputValue);
  // A complete time outside the day stays in the field until focus leaves.
  const timePending = useRef(false);
  const draftRef = useRef({ date: draftDate, unit: draftUnit, live: draftLive });
  draftRef.current = { date: draftDate, unit: draftUnit, live: draftLive };
  // Draft already stored. Playback moves the store date after a save.
  // Close compares with this snapshot so that progress stays.
  const committedDraft = useRef({ date, unit, live: false });
  const flushOnCloseRef = useRef<() => void>(() => {});
  const [month, setMonth] = useState<Date>(() => calendarDay(date, timezone));
  const [viewMode, setViewMode] = useState<PickerView>('day');
  const [browseYear, setBrowseYear] = useState(() => calendarDay(date, timezone).getFullYear());

  const windowFor = (anchor: number, windowUnit: HistogramRange) =>
    HistogramApiHelper.getDateRangeInfo({
      date: rangeRolling && windowUnit !== HISTOGRAM_RANGE.DAY ? Date.now() : anchor,
      range: windowUnit,
      rangeRolling: rangeRolling && windowUnit !== HISTOGRAM_RANGE.DAY,
      shortString: true,
      timezone,
      firstDayOfWeek: firstDayOfWeek as DayOfWeekNumber | undefined,
      locale,
    });

  const committedWindow = windowFor(date, unit);
  const draftWindow = windowFor(draftDate, draftUnit);
  const today = calendarDay(Date.now(), timezone);
  const lastDay =
    maxDate && calendarDay(maxDate, timezone) < today ? calendarDay(maxDate, timezone) : today;
  const maxYear = lastDay.getFullYear();
  const earliestDay = minDate ? calendarDay(minDate, timezone) : undefined;
  const minYear = Math.min(maxYear, earliestDay ? earliestDay.getFullYear() : maxYear - 100);
  const liveIsOn = !!showLiveToggle && isOnInToggle === false;
  // Live playback does not write `date`, so the closed label keeps its own clock.
  const [liveNow, setLiveNow] = useState(() => Date.now());
  useEffect(() => {
    if (!liveIsOn) {
      return;
    }
    setLiveNow(Date.now());
    const timer = window.setInterval(() => setLiveNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, [liveIsOn]);

  useEffect(() => {
    if (!open || !draftLive) {
      return;
    }
    const timer = window.setInterval(() => {
      setDraftDate((current) => {
        const now = Date.now();
        return moment(current).isSame(now, 'minute') ? current : now;
      });
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [open, draftLive]);

  // Both layouts stay mounted. Only the visible one may own the shared test id and Intercom target.
  // Content portals to document.body, so a hidden trigger would leave a floating calendar.
  const { state: sidebarState } = useSidebar();
  const triggerVisible =
    variant === 'collapsed' ? sidebarState === 'collapsed' : sidebarState !== 'collapsed';
  useEffect(() => {
    if (!triggerVisible) {
      flushOnCloseRef.current();
      setOpen(false);
    }
  }, [triggerVisible]);

  const focusDraft = (instant: number) => {
    setDraftDate(instant);
    setMonth(leftCalendarMonth(calendarDay(instant, timezone), earliestDay, true));
  };

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraftUnit(unit);
      committedDraft.current = { date, unit, live: liveIsOn };
      setDraftLive(liveIsOn);
      timePending.current = false;
      setViewMode('day');
      if (liveIsOn) {
        dateBeforeLive.current = date;
        focusDraft(Date.now());
      } else {
        dateBeforeLive.current = null;
        focusDraft(date);
      }
      AnalyticsLogger.openDatePicker({ isOpen: true });
    } else {
      flushOnCloseRef.current();
    }
    setOpen(next);
  };

  const leaveLive = () => {
    setDraftLive(false);
  };

  const draftBounds = () =>
    selectableInstantBounds(minDate?.valueOf(), maxDate?.valueOf(), Date.now());

  const showTime = (instant: number) => {
    const formatted = formatTimeInputValue(instant, timezone);
    timeInputValueRef.current = formatted;
    setTimeInputValue(formatted);
  };

  // Writes the selection now. Compares with the store so a later edit can restore the previous instant.
  const commitDraft = (instant: number, rangeUnit: HistogramRange, live: boolean) => {
    const liveChanged = live !== liveIsOn;
    if (lockDateSelection && !liveChanged) {
      return;
    }

    if (liveChanged) {
      if (live) {
        onGoLive?.();
      } else {
        handleToggleClick?.();
      }
    }

    // Going live replaces the playhead. A locked picker can still commit that switch.
    if (lockDateSelection || live) {
      committedDraft.current = { date: instant, unit: rangeUnit, live };
      return;
    }

    const appliedInstant = clampInstant(instant, draftBounds());
    const instantChanged = appliedInstant !== date;
    if (instantChanged || rangeUnit !== unit) {
      const dateToChange = new Date(appliedInstant);
      const dayChanged = instantChanged && !moment.tz(dateToChange, timezone).isSame(date, 'day');
      if (dayChanged || rangeUnit !== unit) {
        props.onChangeRangeDate?.();
      }

      // onDatePicked already switches a changed day to Day. Write the draft unit
      // unless that switch is still the selection, so a later Week choice is kept.
      props.onDatePicked?.();
      if (instantChanged) {
        dispatch(setFilterDate(dateToChange.valueOf()));
        AnalyticsLogger.selectDateViaDatePicker({ date: dateToChange });
      }
      if (
        shouldCommitDraftRange({
          hasRangeOptions,
          dayPickSwitchesToDay: !!props.onDatePicked,
          dayChanged,
          draftUnitIsDay: rangeUnit === HISTOGRAM_RANGE.DAY,
        })
      ) {
        dispatch(setRange(rangeUnit));
      }
    }

    committedDraft.current = { date: appliedInstant, unit: rangeUnit, live };
  };

  const toggleDraftLive = () => {
    setViewMode('day');
    timePending.current = false;
    if (draftLive) {
      const previous = dateBeforeLive.current;
      dateBeforeLive.current = null;
      setDraftLive(false);
      const restored = previous != null ? previous : draftDate;
      if (previous != null) {
        focusDraft(previous);
      }
      draftRef.current = { date: restored, unit: draftUnit, live: false };
      commitDraft(restored, draftUnit, false);
      return;
    }
    dateBeforeLive.current = draftDate;
    setDraftLive(true);
    const now = Date.now();
    focusDraft(now);
    draftRef.current = { date: now, unit: draftUnit, live: true };
    commitDraft(now, draftUnit, true);
  };

  useEffect(() => {
    if (draftLive || timePending.current) {
      return;
    }
    const formatted = formatTimeInputValue(draftDate, timezone);
    timeInputValueRef.current = formatted;
    setTimeInputValue(formatted);
  }, [draftDate, draftLive, timezone]);

  // Escape and the sidebar hiding this trigger close via `saveOnClose`, which already
  // writes a pending time. Closing also moves focus off the input, firing this same
  // handler through `onBlur`; skip it then so that commit does not run a second time.
  const commitTimeInput = (value: string) => {
    if (!openRef.current) {
      return;
    }
    timePending.current = false;
    const next = instantWithTimeInput(draftDate, timezone, value);
    if (next === undefined) {
      showTime(draftDate);
      return;
    }
    const clamped = clampInstant(next, draftBounds());
    showTime(clamped);
    if (clamped === draftDate) {
      return;
    }
    leaveLive();
    setDraftDate(clamped);
    commitDraft(clamped, draftUnit, false);
  };

  const handleTimeChange = (value: string) => {
    if (lockDateSelection) {
      return;
    }
    timeInputValueRef.current = value;
    setTimeInputValue(value);
    const next = instantWithTimeInput(draftDate, timezone, value);
    if (next === undefined) {
      timePending.current = false;
      return;
    }
    // Blur, Enter, and close write the time. A keystroke only updates the field.
    timePending.current = clampInstant(next, draftBounds()) !== draftDate;
  };

  // setFilterDate replaces a future instant with now. Keep the draft inside the clock.
  useEffect(() => {
    if (!open || draftLive || lockDateSelection) {
      return;
    }
    const { latest } = selectableInstantBounds(undefined, maxDate?.valueOf(), Date.now());
    if (latest !== undefined && draftDate > latest) {
      setDraftDate(latest);
    }
  }, [open, draftLive, lockDateSelection, draftDate, maxDate]);

  const handleDayClick = (day: Date) => {
    if (lockDateSelection) {
      return;
    }
    leaveLive();
    timePending.current = false;
    const nextUnit = rangeRolling ? HISTOGRAM_RANGE.DAY : draftUnit;
    const next = clampInstant(
      instantOnCalendarDay(day, timezone, showTimeInput ? draftDate : undefined).valueOf(),
      draftBounds(),
    );
    setDraftDate(next);
    // A rolling range ignores the picked day, so the click selects Day.
    if (rangeRolling) {
      setDraftUnit(HISTOGRAM_RANGE.DAY);
    }
    commitDraft(next, nextUnit, false);
  };

  // Clicking away saves a time that was still outside the day, and a draft the clock clamped.
  // A draft already stored stays put, so playback can keep moving.
  const saveOnClose = () => {
    if (!openRef.current) {
      return;
    }
    const current = draftRef.current;
    let instant = current.date;
    if (!current.live && timePending.current) {
      const parsed = instantWithTimeInput(instant, timezone, timeInputValueRef.current);
      timePending.current = false;
      if (parsed !== undefined) {
        instant = clampInstant(parsed, draftBounds());
        showTime(instant);
        if (instant !== current.date) {
          setDraftDate(instant);
        }
      }
    }
    const committed = committedDraft.current;
    if (
      !draftNeedsCommitOnClose(
        { instant, unit: current.unit, live: current.live },
        { instant: committed.date, unit: committed.unit, live: committed.live },
      )
    ) {
      return;
    }
    commitDraft(instant, current.unit, current.live);
  };
  flushOnCloseRef.current = saveOnClose;

  const stepUnit = navigateByRange ? unit : HISTOGRAM_RANGE.DAY;
  // Live steps from now. The stored date can still sit on the earliest day.
  const stepFrom = liveIsOn ? liveNow : date;
  const atMinDay = !!minDate && moment(stepFrom).isSameOrBefore(minDate, 'day');
  const atToday = liveIsOn || checkIsToday(date, timezone);
  const stepsDisabled = disabled || lockDateSelection;

  const stepDay = (direction: -1 | 1) => {
    if (stepsDisabled) return;
    if (direction < 0 && atMinDay) return;
    if (direction > 0 && atToday) return;
    props.onDatePicked?.();
    props.onChangeRangeDate?.();
    const stepAnchor = liveIsOn ? Date.now() : undefined;
    if (direction < 0) {
      dispatch(decrementDate({ unit: stepUnit, minDate, date: stepAnchor }));
    } else {
      dispatch(incrementDate({ unit: stepUnit, date: stepAnchor }));
    }
    if (liveIsOn) {
      handleToggleClick?.();
    }
    setOpen(false);
  };

  const labelInstant = liveIsOn ? liveNow : date;
  const labelWindow = liveIsOn ? windowFor(liveNow, HISTOGRAM_RANGE.DAY) : committedWindow;
  // Clock time applies to one day. A week, month, or year label stays on one line without it.
  const labelShowsTime = !!showTimeInput && (liveIsOn || unit === HISTOGRAM_RANGE.DAY);
  const draftShowsTime = !!showTimeInput && draftUnit === HISTOGRAM_RANGE.DAY;
  const typedTime = instantWithTimeInput(draftDate, timezone, timeInputValue);
  const now = Date.now();
  const timeBounds = selectableInstantBounds(minDate?.valueOf(), maxDate?.valueOf(), now);
  const timeErrorKind = draftLive ? undefined : timeInputError(typedTime, timeBounds, now);
  const timeInputInvalid = timeErrorKind !== undefined;
  const timeErrorText = {
    future: locale.date.timeInFuture || 'In the future',
    tooEarly: locale.date.timeTooEarly || 'Too early',
    tooLate: locale.date.timeTooLate || 'Too late',
  };
  const timeError = timeErrorKind ? timeErrorText[timeErrorKind] : undefined;
  const closedLabel = formatPickerLabel(
    labelWithoutWeekday(labelWindow.formatted),
    labelInstant,
    timezone,
    labelShowsTime,
  );
  const accessibleLabel = liveIsOn ? `${locale.general.live} ${closedLabel}` : closedLabel;

  const triggerProps = {
    disabled,
    id: triggerVisible ? 'global-date-picker' : undefined,
    'data-intercom-target': triggerVisible ? 'global-date-picker' : undefined,
    'data-testid': triggerVisible ? 'date-picker-text' : undefined,
    'aria-label': variant === 'collapsed' ? accessibleLabel : closedLabel,
  };

  const trigger =
    variant === 'collapsed' ? (
      <LocationsFilterIconButton {...triggerProps} isActive={open}>
        <CalendarIcon aria-hidden />
      </LocationsFilterIconButton>
    ) : (
      <LocationsFilterButton
        {...triggerProps}
        icon={<CalendarIcon aria-hidden />}
        // Shell draws the border. Override `truncate` so the date and time stay visible.
        className="h-auto min-h-9 rounded-none border-0 bg-transparent px-1 py-2 shadow-none hover:bg-transparent [&>span]:overflow-visible [&>span]:whitespace-normal [&>span]:text-center"
      >
        {liveIsOn && labelShowsTime ? (
          <>
            {labelWithoutWeekday(labelWindow.formatted)} <TickingClock timeZone={timezone} />
          </>
        ) : (
          closedLabel
        )}
      </LocationsFilterButton>
    );

  const dayStep = (direction: -1 | 1) => (
    <button
      type="button"
      className="flex shrink-0 items-center justify-center px-1.5 text-interaction-neutral-content-default disabled:pointer-events-none disabled:opacity-40"
      aria-label={direction < 0 ? locale.general.back : locale.general.next}
      data-testid={direction < 0 ? 'date-picker-prev-day' : 'date-picker-next-day'}
      disabled={stepsDisabled || (direction < 0 ? !!atMinDay : atToday)}
      onClick={(event) => {
        event.stopPropagation();
        stepDay(direction);
      }}
    >
      {direction < 0 ? (
        <ChevronLeft aria-hidden className="size-3.5" />
      ) : (
        <ChevronRight aria-hidden className="size-3.5" />
      )}
    </button>
  );

  const withDaySteps = (control: ReactNode) =>
    variant === 'collapsed' ? (
      control
    ) : (
      <div className="flex w-full min-w-0 items-center rounded-lg border border-border-muted bg-background-surface transition-colors hover:bg-background-muted">
        {dayStep(-1)}
        <div className="min-w-0 flex-1">{control}</div>
        {dayStep(1)}
      </div>
    );

  const restrictedMessage =
    liveIsOn && props.disabled
      ? FeaturesUtil.isLiveViewOnly()
        ? locale.playerMessage.liveViewOnlyRestricted
        : locale.playerMessage.controlRestricted
      : undefined;

  if (restrictedMessage) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex w-full" data-testid="date-picker-container">
            {withDaySteps(trigger)}
          </span>
        </TooltipTrigger>
        <TooltipContent side="right">{restrictedMessage}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {variant === 'collapsed' ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" align="center">
            {liveIsOn ? (
              <>
                {locale.general.live} {labelWithoutWeekday(labelWindow.formatted)}
                {labelShowsTime ? (
                  <>
                    {' '}
                    <TickingClock timeZone={timezone} />
                  </>
                ) : null}
              </>
            ) : (
              accessibleLabel
            )}
          </TooltipContent>
        </Tooltip>
      ) : (
        withDaySteps(<PopoverTrigger asChild>{trigger}</PopoverTrigger>)
      )}
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={8}
        data-testid="date-picker-container"
        className="tw flex w-auto overflow-hidden rounded-xl border border-border p-0 shadow-xl"
      >
        {hasRangeOptions && (
          <div
            className={cn(
              'flex w-32 shrink-0 flex-col gap-1 border-r border-border px-2 py-2',
              lockDateSelection && 'pointer-events-none opacity-40',
            )}
          >
            {rangeOptions.map((option) => {
              const selected = draftUnit === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={lockDateSelection}
                  className={cn(
                    'flex w-full cursor-pointer items-center justify-between gap-2 rounded-sm px-3 py-1.5 text-left text-foreground-primary body-sm',
                    'outline-hidden transition-colors focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                    'disabled:pointer-events-none',
                    selected ? 'bg-background-elevated' : 'hover:bg-background-elevated/60',
                  )}
                  onClick={() => {
                    if (lockDateSelection) {
                      return;
                    }
                    leaveLive();
                    timePending.current = false;
                    setDraftUnit(option.value);
                    commitDraft(draftDate, option.value, false);
                  }}
                  data-testid={`date-picker-range-${option.value}`}
                >
                  <span className="whitespace-nowrap">{option.label}</span>
                  <Check
                    aria-hidden
                    className={cn(
                      'size-4 shrink-0',
                      selected ? 'text-foreground-accent' : 'invisible',
                    )}
                  />
                </button>
              );
            })}
          </div>
        )}

        <div className="flex min-w-[34rem] flex-col px-6 pb-4 pt-5">
          <div className={cn(lockDateSelection && 'pointer-events-none opacity-40')}>
            {viewMode === 'day' ? (
              <Calendar
                mode="range"
                numberOfMonths={2}
                pagedNavigation
                showOutsideDays={false}
                // Months span 4–6 week rows. Always render 6 so paging does not resize the popover.
                fixedWeeks
                month={month}
                onMonthChange={setMonth}
                // Keeps the newest selectable month in the right-hand slot instead of an all-disabled future month.
                endMonth={lastDay}
                startMonth={earliestDay}
                selected={{
                  from: calendarDay(draftWindow.start, timezone),
                  to: calendarDay(draftWindow.end, timezone),
                }}
                onSelect={(_range, day) => handleDayClick(day)}
                disabled={[{ after: lastDay }, ...(earliestDay ? [{ before: earliestDay }] : [])]}
                weekStartsOn={firstDayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined}
                locale={datePickerLocale(locale.localeCode)}
                // The nav bar is stretched across both captions, so without this the title click hits the bar.
                // `\_` keeps the underscore in rdp class names; a bare `_` is a space in Tailwind selectors.
                className="tw mx-auto p-0 [&_.rdp-button\_next]:pointer-events-auto [&_.rdp-button\_previous]:pointer-events-auto [&_.rdp-nav]:pointer-events-none"
                // The Calendar root sets `--cell-size` inline, so a class cannot override it.
                style={{ ['--cell-size' as string]: '2.25rem' }}
                classNames={sidebarCalendarClassNames}
                components={{
                  MonthCaption: ({
                    calendarMonth,
                    displayIndex: _displayIndex,
                    children,
                    ...captionProps
                  }) => (
                    <div {...captionProps}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={captionToggleClassName}
                        onClick={() => {
                          setBrowseYear(calendarMonth.date.getFullYear());
                          setViewMode('month');
                        }}
                      >
                        {children}
                        <ChevronDown className="size-3.5 text-text-muted" aria-hidden />
                      </Button>
                    </div>
                  ),
                }}
              />
            ) : (
              <PeriodJump
                viewMode={viewMode}
                browseYear={browseYear}
                minYear={minYear}
                maxYear={maxYear}
                minDay={earliestDay}
                maxDay={lastDay}
                selectedMonth={calendarDay(draftDate, timezone)}
                localeCode={locale.localeCode}
                onBrowseYear={setBrowseYear}
                onShowMonths={() => setViewMode('month')}
                onShowYears={() => setViewMode('year')}
                onPickMonth={(monthIndex) => {
                  const picked = new Date(browseYear, monthIndex, 1);
                  const lastMonth = new Date(lastDay.getFullYear(), lastDay.getMonth(), 1);
                  const onRight = picked.getTime() >= lastMonth.getTime();
                  setMonth(leftCalendarMonth(onRight ? lastMonth : picked, earliestDay, onRight));
                  setViewMode('day');
                }}
              />
            )}
          </div>

          {lockDateSelection && (
            <p className="mt-4 text-foreground-secondary body-sm">
              {locale.playerMessage.controlRestricted}
            </p>
          )}

          <div className="-mx-6 mt-5 flex items-center gap-2 border-t border-border pl-4 pr-6 pt-4">
            {draftShowsTime ? (
              <>
                <div className="flex shrink-0 items-center gap-2">
                  {onGoLive && (
                    <Button
                      type="button"
                      variant={draftLive ? 'default' : 'secondary'}
                      size="sm"
                      className="shrink-0"
                      aria-pressed={draftLive}
                      onClick={toggleDraftLive}
                      data-testid="date-picker-go-live"
                    >
                      {locale.general.live}
                    </Button>
                  )}
                  {draftLive ? (
                    <span
                      className="shrink-0 text-sm font-normal leading-5 text-foreground-primary"
                      data-testid="date-picker-time"
                    >
                      <TickingClock timeZone={timezone} />
                    </span>
                  ) : (
                    <DatePickerTimeInput
                      value={timeInputValue}
                      onChange={handleTimeChange}
                      onCommit={commitTimeInput}
                      disabled={lockDateSelection}
                      invalid={timeInputInvalid}
                      ariaInvalid={timeInputInvalid}
                      ariaDescribedBy={
                        triggerVisible && timeError ? 'date-picker-time-error' : undefined
                      }
                      ariaLabel={locale.date.time}
                      dataTestId="date-picker-time"
                      timezone={formatTimezone(draftDate, timezone)}
                      timezoneTestId="date-picker-timezone"
                    />
                  )}
                  {draftLive && (
                    <span
                      className="shrink-0 text-foreground-primary body-sm"
                      data-testid="date-picker-timezone"
                    >
                      {formatTimezone(draftDate, timezone)}
                    </span>
                  )}
                </div>
                {timeError && (
                  <span
                    id={triggerVisible ? 'date-picker-time-error' : undefined}
                    role="alert"
                    className="shrink-0 whitespace-nowrap text-foreground-red body-sm"
                    data-testid={triggerVisible ? 'date-picker-time-error' : undefined}
                  >
                    {timeError}
                  </span>
                )}
              </>
            ) : (
              <span className="text-foreground-secondary body-sm">
                {labelWithoutWeekday(draftWindow.formatted)}
              </span>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default GlobalDatePicker;
